import { FormGroup } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { ManagerAuthorization, ValidationErrorCode } from '@common/enums/';
import {
    nzaServiceDeclarationRuleAddSchema,
    nzaServiceDeclarationRuleEditSchema,
} from '@common/validation/nzaServiceDeclarationRule';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import { Button, DangerAlertWhenErrorCode, FormContainer, Header, Loading, SaveSubmit } from '../../elements/common';
import { FormContextProvider } from '../../elements/formContext';
import { DateField } from '../../fields/Date';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';
import { NzaServiceDeclarationActivityList } from '../nzaServiceDeclarationActivities/NzaServiceDeclarationActivityList';
import { NzaServiceDeclarationSettingList } from '../nzaServiceDeclarationSettings/NzaServiceDeclarationSettingList';

interface INzaServiceDeclarationRuleAddEditProps {
    serviceId: string;
}

interface INzaServiceDeclarationRule {
    exportUntilDate: string | null;
    id?: string;
    serviceId: string;
}

export function NzaServiceDeclarationRuleAddEdit(props: INzaServiceDeclarationRuleAddEditProps) {
    const [nzaServiceDeclarationRule, setNzaServiceDeclarationRule] = useState<INzaServiceDeclarationRule>({
        exportUntilDate: null,
        serviceId: '',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState(emptyValidationErrors);
    const [errorCode, setErrorCode] = useState<string>();
    const canEdit = hasOneAuthorization(ManagerAuthorization.serviceDeclarationRules);

    assertHasAuthorization(ManagerAuthorization.servicesRead);

    useEffect(() => {
        if (props.serviceId) {
            load();
        } else {
            throw new Error('service id is undefined');
        }
    }, []);

    return (
        <FixedWidthTemplate>
            <Header content={translateKey('serviceDeclarationRules.serviceDeclarationRules')} />
            <DangerAlertWhenErrorCode errorCode={errorCode} />
            <Loading
                item={nzaServiceDeclarationRule}
                loaded={(loadedNzaServiceDeclarationRule) => (
                    <>
                        <FormContainer onSubmit={async () => await save()}>
                            <FormContextProvider
                                value={{
                                    errors: errors,
                                    item: loadedNzaServiceDeclarationRule,
                                    onChange: async (key, value) => await handleChange(key, value),
                                    onDoValidate: async (key) => await validateOne(key),
                                    placeholderGroupName: '',
                                }}
                            >
                                {canEdit ? (
                                    <DateField
                                        fieldKey="exportUntilDate"
                                        label={translateKey('serviceDeclarationRules.exportUntilDate')}
                                        placeholder={translateKey('serviceDeclarationRules.exportUntilDate')}
                                        size="medium"
                                        tooltipText={translateKey('serviceDeclarationRules.exportUntilDateTooltip')}
                                    />
                                ) : (
                                    <FormGroup label={translateKey('serviceDeclarationRules.exportUntilDate')}>
                                        <NullableFormattedDate
                                            value={loadedNzaServiceDeclarationRule.exportUntilDate}
                                        />
                                    </FormGroup>
                                )}
                            </FormContextProvider>

                            {canEdit && (
                                <>
                                    <Button
                                        color="secondary"
                                        disabled={isSaving}
                                        large
                                        left
                                        onClick={async () => load()}
                                        outlined
                                    >
                                        {translateKey('common.cancel')}
                                    </Button>
                                    <SaveSubmit disabled={isSaving} />
                                </>
                            )}
                        </FormContainer>

                        {loadedNzaServiceDeclarationRule.id && (
                            <>
                                <NzaServiceDeclarationSettingList
                                    nzaServiceDeclarationRuleId={loadedNzaServiceDeclarationRule.id}
                                    serviceId={loadedNzaServiceDeclarationRule.serviceId}
                                />
                                <NzaServiceDeclarationActivityList
                                    nzaServiceDeclarationRuleId={loadedNzaServiceDeclarationRule.id}
                                    serviceId={loadedNzaServiceDeclarationRule.serviceId}
                                />
                            </>
                        )}
                    </>
                )}
            />
        </FixedWidthTemplate>
    );

    async function handleChange<K extends keyof INzaServiceDeclarationRule>(
        name: K,
        value: INzaServiceDeclarationRule[K]
    ) {
        setNzaServiceDeclarationRule({
            ...nzaServiceDeclarationRule,
            [name]: value,
        });
    }

    async function validateOne(name: keyof INzaServiceDeclarationRule) {
        setErrors(
            updateValidationErrorsForOne(nzaServiceDeclarationRuleAddSchema, nzaServiceDeclarationRule, errors, name)
        );
    }

    async function load() {
        const url = `/api/nzaServiceDeclarationRules/${props.serviceId}`;
        const result = await fetchJson(url, 'GET');

        if (result.json.id) {
            setNzaServiceDeclarationRule(result.json);
        } else {
            setNzaServiceDeclarationRule({
                exportUntilDate: null,
                serviceId: '',
            });
        }
    }

    async function save() {
        let url = '/api/nzaServiceDeclarationRules/';

        if (nzaServiceDeclarationRule.id) {
            url += nzaServiceDeclarationRule.id;
        } else {
            if (props.serviceId) {
                setNzaServiceDeclarationRule({ ...nzaServiceDeclarationRule, serviceId: props.serviceId });
            }
        }

        const { errors: validateErrors, item } = validate(
            nzaServiceDeclarationRule.id ? nzaServiceDeclarationRuleEditSchema : nzaServiceDeclarationRuleAddSchema,
            nzaServiceDeclarationRule
        );
        setErrors(validateErrors);
        if (validateErrors.size > 0) {
            return;
        }

        setIsSaving(true);

        const result = await fetchJson(url, 'POST', item, [ValidationErrorCode.DUPLICATE_RECORD]);
        if (result.errorCode) {
            setErrorCode(result.errorCode);
            setIsSaving(false);
        } else {
            setNzaServiceDeclarationRule({ ...nzaServiceDeclarationRule, id: result.json.id });
            setIsSaving(false);
        }
    }
}
